import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import PlatformDetector from '../../../../utils/PlatformDetector';
import { colores } from '../../../../styles/variablesCss';
import { useBanners } from '../../../BannerManager';

// Función para capitalizar solo la primera letra
const capitalizeFirstLetter = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const BannerBotonesHeader = () => {
  const navigation = useNavigation();
  const screenWidth = Dimensions.get('window').width;
  const isSmallScreen = screenWidth < 600;
  const { banners: allBanners, loading, error, isWeb } = useBanners();
  const bannersHeader = allBanners.fila1 || [];

  // Crear categorías directamente de los banners de fila1
  const categories = bannersHeader.map((banner, index) => {
    return {
      name: capitalizeFirstLetter(banner.titulo || `Categoría ${index + 1}`),
      banner: banner, // Guardar el banner completo para acceder a su codBanner
      params: { 
        D: index, // Usar el índice como identificador de categoría
        C: 0, 
        S: 0, 
        searchText: index === 0 ? 'ofertas' : '' // Primera categoría mantiene 'ofertas' como searchText
      }
    };
  });
  

  const handleCategoryPress = (params, banner) => {
    const isDesktop = screenWidth >= 600;
    // Determinar si abrir en pestaña nueva basado en el campo apertura (0 = misma pestaña, 1 = nueva pestaña)
    const abrirEnPestañaNueva = banner?.apertura === 1;
    
    // Si el banner tiene un link definido, redirigir a ese link
    if (banner?.link && banner.link.trim() !== '') {
      if (PlatformDetector.isWeb() && typeof window !== 'undefined') {
        // Extraer solo la parte de la URL después del dominio
        const linkUrl = banner.link.includes('pinguino.com.ar') 
          ? banner.link.split('pinguino.com.ar')[1] 
          : banner.link;
        
        // Determinar si es URL externa
        const isExternalUrl = banner.link.startsWith('http://') || banner.link.startsWith('https://');
        const finalUrl = isExternalUrl ? banner.link : (window.location.origin + linkUrl);
        
        // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
        if (abrirEnPestañaNueva && isDesktop) {
          window.open(finalUrl, '_blank', 'noopener,noreferrer');
          return;
        }
        
        // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
        if (isExternalUrl) {
          window.location.href = finalUrl;
        } else {
          const isOnProductsPage = window.location.pathname === '/productos';
          
          if (isOnProductsPage) {
            window.history.pushState({}, '', linkUrl);
            window.dispatchEvent(new Event('navigationChange'));
          } else {
            window.location.href = finalUrl;
          }
        }
      } else {
        // En móvil, abrir el link (puede requerir ajustes según tu navegación)
        navigation.navigate('Productos', params);
      }
      return;
    }

    // Si no hay link, usar la lógica actual de navegación
    if (PlatformDetector.isWeb() && typeof window !== 'undefined') {
      // Construir URL con parámetros para navegación web
      const urlParams = new URLSearchParams();
      
      // Siempre incluir D, C, S como 0 (por defecto)
      urlParams.append('D', 0);
      urlParams.append('C', 0);
      urlParams.append('S', 0);
      
      // Agregar codBanner usando el ID del banner si está disponible
      urlParams.append('codBanner', banner?.codBanner || 0);
      urlParams.append('searchText', '');
      
      // Construir URL completa
      const url = `/productos?${urlParams.toString()}`;
      
      // Si apertura es 0, abrir en pestaña nueva (solo en desktop)
      if (abrirEnPestañaNueva && isDesktop) {
        const fullUrl = window.location.origin + url;
        window.open(fullUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      
      // Si apertura es 1 o estamos en móvil, navegar en la misma pestaña
      const isOnProductsPage = window.location.pathname === '/productos';
      
      if (isOnProductsPage) {
        window.history.pushState({}, '', url);
        window.dispatchEvent(new Event('navigationChange'));
      } else {
        window.location.href = url;
      }
    } else {
      // En plataformas móviles o React Native, usar la navegación tradicional
      navigation.navigate('Productos', params);
    }
  };

  return (
    <View style={styles.container}>
      {isSmallScreen ? (
        // Para pantallas pequeñas, mantener el scroll horizontal
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          style={{ flex: 1 }}
        >
          {categories.map((category, index) => (
            <ButtonWithHover
              key={index}
              style={[
                styles.categoryItem,
                styles.categoryItemSmall,
                index === 0 && styles.firstButtonGreen,
                index < categories.length - 1 && {borderRightWidth: 1, borderRightColor: colores.blanco},
              ]}
              hoverStyle={index === 0 ? styles.hoverEffectGreen : styles.hoverEffect}
              onPress={() => handleCategoryPress(category.params, category.banner)}
              activeOpacity={0.7}
            >
              <Text style={[styles.categoryText, styles.categoryTextSmall]}>
                {category.name}
              </Text>
            </ButtonWithHover>
          ))}
        </ScrollView>
      ) : (
        // Para pantallas grandes, distribuir uniformemente
        <View style={styles.desktopContainer}>
          {/* Primero renderizar los botones */}
          {categories.map((category, index) => (
            <ButtonWithHover
              key={`button-${index}`}
              style={[styles.desktopCategoryItem, index === 0 && styles.firstButtonGreen]}
              hoverStyle={index === 0 ? styles.hoverEffectGreen : styles.hoverEffect}
              onPress={() => handleCategoryPress(category.params, category.banner)}
              activeOpacity={0.7}
            >
              <Text style={styles.categoryText}>{category.name}</Text>
            </ButtonWithHover>
          ))}
          
          {/* Luego renderizar los separadores por encima, fuera de los botones */}
          {categories.slice(0, -1).map((_, index) => (
            <View 
              key={`divider-${index}`} 
              style={[styles.categoryDivider, { left: `${((index + 1) / categories.length) * 100}%` }]} 
            />
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: 40,
    width: '100%',
    backgroundColor: colores.rojo,
    overflow: 'hidden', // Evitar que los elementos se salgan del contenedor
  },
  scrollContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    height: '100%',
    minWidth: '100%',
    width: '100%',
    justifyContent: 'space-between',
    gap: 8,
  },
  desktopContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between', // Distribuir uniformemente
    paddingHorizontal: 5,
    height: '100%',
    width: '100%',
  },
  categoryItem: {
    paddingHorizontal: 8,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  categoryItemSmall: {
    flexGrow: 1,          // ocupa el ancho disponible cuando hay pocos
    flexShrink: 0,        // evita que se comprima demasiado
    flexBasis: 0,         // distribuir espacio equitativamente
    minWidth: 90,         // asegura scroll horizontal cuando hay muchos
  },
  desktopCategoryItem: {
    flex: 1,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative', // Necesario para el pseudo-hover
    // Aplicar estilos específicos para web
    ...(PlatformDetector.isWeb() && {
      transition: 'all 0.25s cubic-bezier(0.175, 0.1, 0.32, 1.275)', // Transición más dinámica
    }),
  },
  // Crear un borde como un elemento separado totalmente fijo
  categoryDivider: {
    position: 'absolute',
    width: 1,
    height: '70%',
    top: '15%', // Centrado verticalmente
    backgroundColor: colores.blanco, // Línea blanca separadora
    transform: [{ translateX: -0.5 }], // Ajuste para centrar el borde de 1px
    zIndex: 10, // Mayor z-index para asegurar que esté por encima de todo
    pointerEvents: 'none', // Evita que el borde capture eventos del mouse
  },
  // Eliminar el borde del último elemento
  lastCategoryItem: {
    borderRightWidth: 0,
  },
  // Estilo para el primer botón (rojo especial)
  firstButtonGreen: {
    backgroundColor: '#c82828',
  },
  // Efecto hover para la versión web y tactil en móvil
  hoverEffect: {
    ...(PlatformDetector.isWeb() && {
      backgroundColor: '#cd0f1e', // Fondo rojo oscuro al hover/click
    }),
    ...(!PlatformDetector.isWeb() && {
      // En móvil aplicamos el mismo efecto durante el press
    }),
  },
  // Efecto hover para el botón rojo especial
  hoverEffectGreen: {
    ...(PlatformDetector.isWeb() && {
      backgroundColor: '#a02020', // Rojo más oscuro al hover
    }),
  },
  categoryText: {
    fontSize: 14,
    fontWeight: 'bold',
    textTransform: 'none',
    color: colores.blanco,
    textAlign: 'center',
    // Aplicar estilos específicos para web
    ...(PlatformDetector.isWeb() && {
      cursor: 'pointer',
    }),
  },
  // Texto cuando el botón está en estado hover
  categoryTextHover: {
    fontWeight: '600',
  },
  // Agregamos un efecto de línea inferior roja al hacer hover
  underlineEffect: {
    borderBottomWidth: 2,
    borderBottomColor: colores.rojo,
    paddingBottom: 3,
  },
  // Contenedor para el texto con efecto de underline
  textContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Eliminado el efecto de punto */

  categoryTextSmall: {
    fontSize: 12,
  }
});

// Componente auxiliar para manejar efectos hover en Web
const ButtonWithHover = ({ style, hoverStyle, children, onPress, activeOpacity }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Solo aplicar eventos hover si estamos en web
  const hoverProps = PlatformDetector.isWeb() 
    ? {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
      }
    : {};
  
  // Clonar los hijos para aplicarles estilos de hover
  const enhancedChildren = React.Children.map(children, child => {
    // Verificar si el child es un elemento válido y podría ser un Text component
    if (React.isValidElement(child) && child.props && child.props.style) {
      // Agregar estilos de hover al texto
      return React.cloneElement(child, {
        style: [
          ...(Array.isArray(child.props.style) ? child.props.style : [child.props.style]),
          isHovered && styles.categoryTextHover
        ]
      });
    }
    return child;
  });
    
  return (
    <TouchableOpacity
      style={[style, isHovered && hoverStyle]}
      onPress={onPress}
      activeOpacity={activeOpacity}
      onPressIn={() => setIsHovered(true)}
      onPressOut={() => setIsHovered(false)}
      {...hoverProps}
    >
      <View style={styles.textContainer}>
        {enhancedChildren}
      </View>
    </TouchableOpacity>
  );
};

export default BannerBotonesHeader;
